import axios, {AxiosError} from "axios";
import Swal from "sweetalert2";
import {ThemeColors} from "./colors";
import {AppView, AppViewToLinkMap} from "./App";
import {hardNavigate} from "./contexts/NavContext/nav-service";

type WithErrorOpts = {
    titleQualifier?: string;
    overrideMessage?: string;
    navigateTo?: (newView: { view: AppView }) => void; // Use this ->  const {navigateTo} = useNavigationContext()!;
};

export async function withApiErrorHandler<T>(
    fn: () => T | Promise<T>,
    opts: WithErrorOpts = {}
): Promise<T | null> {
    try {
        return await fn();
    } catch (err: unknown) {
        await fireSwalFromError(err, opts);
        return null;
    }
}

export async function withApiErrorHandlerThrow<T>(
    fn: () => T | Promise<T>,
    opts: WithErrorOpts = {}
): Promise<T> {
    try {
        return await fn();
    } catch (err: unknown) {
        await fireSwalFromError(err, opts);
        throw err; // preserve original error
    }
}

interface FireSwalOptions {
    titleQualifier?: string;
    overrideMessage?: string;
    navigateTo?: (newView: { view: AppView }) => void;
}


export async function fireSwalFromError(
    error: AxiosError | unknown,
    opts: FireSwalOptions = {},
): Promise<void> {
    const {titleQualifier, overrideMessage: customMessage, navigateTo} = opts;
    let overrideMessage = customMessage;

    if (!axios.isAxiosError(error)) {
        // Non-Axios error
        await Swal.fire({
            title: titleQualifier ?? "Error",
            icon: "error",
            text: `Unexpected error occurred: ${String(error)}`,
            confirmButtonColor: ThemeColors.neutralDarkHex,
        });
        throw error;
    }

    const axiosError = error as AxiosError;
    const status = axiosError.response?.status;
    const data = axiosError.response?.data;

    // --- XML body handling (like GCS/S3) ---
    let xmlCode: string | undefined;
    let xmlMessage: string | undefined;
    if (typeof data === "string" && data.trim().startsWith("<")) {
        try {
            const doc = new DOMParser().parseFromString(data, "application/xml");
            xmlCode = doc.querySelector("Code")?.textContent ?? undefined;
            xmlMessage = doc.querySelector("Message")?.textContent ?? undefined;
        } catch {
            /* ignore parsing errors */
        }
    }

    // If it’s a 422 with a detail array, build a nice joined message
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    if (status === 422 && Array.isArray((data as any)?.detail)) {
        overrideMessage = (data.detail as Array<{ loc: string | string[]; msg: string }>)
            .map(err => {
                const loc = Array.isArray(err.loc) ? err.loc.join(".") : err.loc;
                return `${loc}: ${err.msg}`;
            })
            .join("\n");
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
    } else if (!overrideMessage && typeof (data as any)?.detail === "string") {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        overrideMessage = (data as any).detail;
    }

    let title = titleQualifier ?? "Error";
    let message = overrideMessage ?? "";

    if (xmlCode || xmlMessage) {
        message = [
            message,
            xmlCode ? `Code: ${xmlCode}` : null,
            xmlMessage ? `Message: ${xmlMessage}` : null,
        ].filter(Boolean).join("\n");
    } else if (!message) {
        message = "Something went wrong.";
    }

    if (status) {
        title = `${title}: Error ${status}`;
    }

    await Swal.fire({
        title,
        text: message,
        icon: "error",
        confirmButtonColor: ThemeColors.neutralDarkHex,
    });

    if (status == 401) {
        if (navigateTo) {
            setTimeout(() => {
                navigateTo({view: AppView.Login});
            }, 0);
        } else {
            hardNavigate(AppViewToLinkMap[AppView.Login], {replace: false});
        }
    } else {
        throw axiosError;
    }
}


export async function toAxiosLikeError(
    res: Response,
    context: string
): Promise<AxiosError> {
    const body = await res.text().catch(() => "");

    // Try to pull <Code> and <Message> if XML (like GCS / S3 errors)
    let xmlCode: string | undefined;
    let xmlMessage: string | undefined;
    if (body.trim().startsWith("<")) {
        try {
            const doc = new DOMParser().parseFromString(body, "application/xml");
            xmlCode = doc.querySelector("Code")?.textContent ?? undefined;
            xmlMessage = doc.querySelector("Message")?.textContent ?? undefined;
        } catch {
            /* ignore */
        }
    }

    const axiosLike: Partial<AxiosError> = {
        name: "AxiosError",
        message:
            `${context}: ${res.status} ${res.statusText}` +
            (xmlMessage ? ` — ${xmlMessage}` : ""),
        code: String(res.status),
        isAxiosError: true,
        config: {},
        response: {
            status: res.status,
            statusText: res.statusText,
            headers: {}, // not available from fetch
            config: {},
            data: body,
        },
        toJSON: () => ({
            message: context,
            status: res.status,
            code: xmlCode,
            xmlMessage,
            data: body,
        }),
    };

    return axiosLike as AxiosError;
}
